import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { GET_DOMAINS_INFINITE } from '../../../../query/monitor/data/sources/keys';
import { getDomainsStats } from '../../../../../services/monitor/data/sources/MonitorDataSourcesService';
import { ChatType } from '../../../../../types/monitor/ChatType';
import { DomainsStatsResponse } from '../../../../../models/monitor/data/sources/DomainsStatsResponse';
import { useMemo } from 'react';

const PAGE_SIZE = 20;

export const useGetDomainsInfinite = (
  projectId: number | null,
  chatType: ChatType | null,
  from: Date | null,
  to: Date | null,
  domain: string[] | undefined | null
) => {
  const queryClient = useQueryClient();
  const queryEnabled = !!projectId && !!chatType && !!from && !!to && domain !== null;

  const fromTimestamp = from?.getTime() ?? null;
  const toTimestamp = to?.getTime() ?? null;

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isError, error } =
    useInfiniteQuery<DomainsStatsResponse>({
      queryKey: [GET_DOMAINS_INFINITE, projectId, chatType, fromTimestamp, toTimestamp, domain],
      queryFn: ({ pageParam = 1 }) =>
        getDomainsStats(projectId!, true, chatType!, from!, to!, pageParam as number, PAGE_SIZE, domain),
      getNextPageParam: (lastPage) => {
        if (!lastPage.pagination) {
          return undefined;
        }

        const nextPage = lastPage.pagination.currentPage + 1;
        return nextPage <= lastPage.pagination.totalPages ? nextPage : undefined;
      },
      initialPageParam: 1,
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      enabled: queryEnabled,
    });

  const allLoadedDomains = useMemo(() => {
    if (!data?.pages) {
      return [];
    }

    return data.pages.flatMap((page) => page.domains.map((d) => d.domain));
  }, [data]);

  const allOptions = useMemo(() => {
    return allLoadedDomains.map((domain) => ({
      name: domain,
      domain: domain,
    }));
  }, [allLoadedDomains]);

  return {
    allOptions,
    allLoadedDomains,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: queryEnabled ? isLoading : false,
    isError: queryEnabled ? isError : false,
    error: queryEnabled ? error : null,
    invalidate: () =>
      queryClient.invalidateQueries({
        queryKey: [GET_DOMAINS_INFINITE, projectId, chatType, fromTimestamp, toTimestamp, domain],
        refetchType: 'active',
      }),
  };
};
